/**
 * Players page: where a player came from, turned into what the roster card and the detail sheet show, plus the
 * page's "came from" filter (`?campaign=` / `?landing_page=`, see `PLAYERS_FROM_PARAMS`).
 *
 * The resolution itself is `resolveAttribution` (`creatives/link-attribution.ts`): a player's stored `campaign` is a
 * tracking link's token (one asset version, and the landing page it goes through, if any), an affiliates-platform
 * campaign number, a no-link value, or something the brand's links do not know. This module only adds the page's
 * display rules on top: labels (literal translate calls, so the extractor harvests them), deep-link paths, and the
 * two "nothing to say yet" states.
 */
import type { TFunction } from 'i18next';

import type { Player } from '../../mock-data/types';
import {
  affiliatesPath,
  campaignsLandingOn,
  linkAssetName,
  linkAssetPath,
  linkKindLabel,
  linkLandingPage,
  resolveAttribution,
} from '../creatives/link-attribution';
import { versionStamp } from '../creatives/tracking-link';
import type { TrackingLink, TrackingLinkKind } from '../creatives/tracking-links-api';

/** What a "Came from" cell shows. */
export type CameFrom =
  /** A Studio tracking link: its asset (deep link to the composer) and the landing page it goes through, if any. A
   *  `path` is left out where that asset kind's pages are switched off (they would answer Not Found). */
  | { kind: 'link'; kindLabel: string; name: string; path?: string; via?: { name: string; path?: string } }
  /** An affiliates-platform campaign (opens the Affiliates page). `named`: the affiliate who brought the player and
   *  the campaign (its name, else its number), when the brand's affiliates list names that affiliate; without it
   *  `label` says which campaign. */
  | { kind: 'affiliate'; label: string; path: string; named?: { affiliate: string; campaign: string } }
  /** No tracked link. */
  | { kind: 'direct' | 'organic'; label: string }
  /** A campaign the brand's links do not know, shown as stored. */
  | { kind: 'other'; label: string }
  /** The brand's links are still being read, so an unknown value may yet resolve to a link. */
  | { kind: 'pending' }
  /** No registration row (an activity-only player): nothing was recorded, so nothing is claimed. */
  | { kind: 'none' };

/** Affiliates-platform names, keyed by number: affiliates by `affiliate_no` (the player's stored `affiliate`),
 *  campaigns by `campaign_no` (their stored `campaign`). Empty until the brand's affiliates list is read.
 *  `affiliateIds`: the platform id per `affiliate_no`, so the "came from" link opens that affiliate. */
export type AffiliateNames = {
  affiliates: ReadonlyMap<string, string>;
  campaigns: ReadonlyMap<string, string>;
  affiliateIds?: ReadonlyMap<string, string>;
};

/** No affiliates-platform names (none read, or the brand has no affiliate players). */
export const NO_AFFILIATE_NAMES: AffiliateNames = { affiliates: new Map(), campaigns: new Map() };

/** The landing page part of a link's "came from": the page it goes through (a landing page's own link is the page
 *  itself, already named as the asset, so it has none). */
const viaOf = (
  link: TrackingLink,
  t: TFunction,
  opens: (kind: TrackingLinkKind) => boolean,
): { name: string; path?: string } | undefined => {
  if (link.kind === 'landing_page') return undefined;
  const lp = linkLandingPage(link);
  if (!lp) return undefined;
  const name = lp.name.trim() || t('Untitled');
  return opens('landing_page') ? { name, path: lp.path } : { name };
};

/** A player's "came from". `linksPending`: the brand's links have not been read yet. `opens`: whether an asset kind's
 *  pages are switched on (see `assetKindOpens`), so a deep link never leads to Not Found. `names`: the brand's
 *  affiliates and campaigns, to say which affiliate brought an affiliate-link player. */
export const cameFromOf = (
  player: Player,
  byCampaign: ReadonlyMap<string, TrackingLink>,
  linksPending: boolean,
  t: TFunction,
  opens: (kind: TrackingLinkKind) => boolean,
  names: AffiliateNames = NO_AFFILIATE_NAMES,
): CameFrom => {
  // Same presence rule as the sheet's "Never" sign-in: only a player with a registration row has a recorded campaign
  // (blank there means no link); an activity-only row says nothing either way.
  if (player.registeredAt === undefined && player.campaign === undefined) return { kind: 'none' };
  const attribution = resolveAttribution(player.campaign, byCampaign);
  switch (attribution.kind) {
    case 'link': {
      const { link } = attribution;
      return {
        kind: 'link',
        kindLabel: linkKindLabel(link, t),
        name: linkAssetName(link) || t('Untitled'),
        ...(opens(link.kind) ? { path: linkAssetPath(link) } : {}),
        via: viaOf(link, t, opens),
      };
    }
    case 'affiliate': {
      const { campaignNo } = attribution;
      // The stored `affiliate` names someone only when the affiliates list knows that number (a direct signup's
      // default `1` never matches), so an unknown or unread one keeps the campaign-only label.
      const affiliate = player.affiliate ? names.affiliates.get(player.affiliate) : undefined;
      // The affiliate's own sheet on the Affiliates page when the list knows them, else the page itself.
      const affiliateId = player.affiliate ? names.affiliateIds?.get(player.affiliate) : undefined;
      return {
        kind: 'affiliate',
        label: t('Affiliate campaign #{{n}}', { n: campaignNo }),
        path: affiliatesPath(affiliateId),
        ...(affiliate ? { named: { affiliate, campaign: names.campaigns.get(campaignNo) ?? `#${campaignNo}` } } : {}),
      };
    }
    case 'organic':
      return { kind: 'organic', label: t('Organic') };
    case 'direct':
      return { kind: 'direct', label: t('Direct') };
    default:
      return linksPending ? { kind: 'pending' } : { kind: 'other', label: attribution.campaign };
  }
};

/* ---------- the "came from" filter ---------- */

/** The label of the `?campaign=` chip, or undefined while the links that could name it are still being read. */
export const campaignChipLabel = (
  campaign: string,
  byCampaign: ReadonlyMap<string, TrackingLink>,
  linksPending: boolean,
  t: TFunction,
): string | undefined => {
  const attribution = resolveAttribution(campaign, byCampaign);
  switch (attribution.kind) {
    case 'link': {
      // The asset's name and, when the key carries one, the version's time: an asset's versions share its name.
      const stamp = versionStamp(attribution.link.version_key);
      const name = linkAssetName(attribution.link) || t('Untitled');
      return t('From: {{name}}', { name: stamp ? `${name} · ${stamp}` : name });
    }
    case 'affiliate':
      return t('Affiliate campaign #{{n}}', { n: attribution.campaignNo });
    case 'organic':
      return t('From: {{name}}', { name: t('Organic') });
    case 'direct':
      return t('From: {{name}}', { name: t('Direct') });
    default:
      return linksPending ? undefined : t('From: {{name}}', { name: attribution.campaign });
  }
};

/** The name of a landing page from the brand's links (its own link, else any link going through it), or undefined
 *  when no link lands there. Pure. */
export const landingPageNameOf = (links: readonly TrackingLink[], landingPageId: string): string | undefined => {
  const own = links.find((l) => l.kind === 'landing_page' && l.creative_id === landingPageId);
  if (own) return linkAssetName(own);
  const through = links.find((l) => l.landing_page_id === landingPageId && l.landing_page?.name);
  return through?.landing_page?.name;
};

/** The label of the `?landing_page=` chip, or undefined while the links are still being read. */
export const landingChipLabel = (
  links: readonly TrackingLink[],
  landingPageId: string,
  linksPending: boolean,
  t: TFunction,
): string | undefined => {
  if (linksPending) return undefined;
  return t('Through: {{name}}', { name: landingPageNameOf(links, landingPageId)?.trim() || t('this landing page') });
};

/** The campaign sets the URL's "came from" filter narrows the roster to (AND-ed; see `filterByCampaignSets`). A
 *  landing page filter is left out when the links could not be read (it cannot be resolved; the page says so). */
export const campaignSetsOf = (
  campaign: string,
  landingPageId: string,
  links: readonly TrackingLink[],
  linksFailed: boolean,
): Set<string>[] => {
  const sets: Set<string>[] = [];
  if (campaign) sets.push(new Set([campaign]));
  if (landingPageId && !linksFailed) sets.push(new Set(campaignsLandingOn(links, landingPageId)));
  return sets;
};
